import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, CalendarDays } from "lucide-react";
import { api, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Badge, Loading } from "@/components/kit";

export default function Events() {
  const { activeStore } = useAuth();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ name: "", day: "", kind: "event", expected_attendance: "", distance_km: "", multiplier: 1.2 });

  const load = () => {
    if (!activeStore) return;
    setRows(null);
    api.get("/events", { params: { store_id: activeStore } }).then(({ data }) => setRows(data));
  };
  useEffect(load, [activeStore]);

  const add = async (e) => {
    e.preventDefault();
    if (!f.name || !f.day) return toast.error("Name and date required");
    try {
      await api.post("/events", { store_id: activeStore, ...f });
      toast.success("Event added — forecasts will include its impact");
      setF({ ...f, name: "", day: "", expected_attendance: "", distance_km: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add event");
    }
  };

  if (!rows) return <Loading />;

  return (
    <div>
      <PageHeader title="Events & Holidays" subtitle="Add events so the forecasting engine can factor in demand spikes" />

      <Card className="p-5 mb-6" testid="event-form">
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <L label="Name" className="md:col-span-2"><input data-testid="event-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="inp" placeholder="Rugby match" /></L>
          <L label="Date"><input type="date" data-testid="event-day" value={f.day} onChange={(e) => setF({ ...f, day: e.target.value })} className="inp" /></L>
          <L label="Type">
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="inp">
              <option value="event">Event</option><option value="holiday">Public Holiday</option>
              <option value="school_holiday">School Holiday</option><option value="promotion">Promotion</option>
            </select>
          </L>
          <L label="Multiplier"><input type="number" step="0.05" data-testid="event-mult" value={f.multiplier} onChange={(e) => setF({ ...f, multiplier: e.target.value })} className="inp" /></L>
          <L label="Attendance"><input type="number" min="0" placeholder="Optional" value={f.expected_attendance} onChange={(e) => setF({ ...f, expected_attendance: e.target.value })} className="inp" /></L>
          <button data-testid="add-event-btn" className="inline-flex items-center justify-center gap-2 bg-zinc-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-zinc-800 transition-colors duration-200"><Plus className="h-4 w-4" /> Add</button>
        </form>
      </Card>

      <Card className="overflow-hidden" testid="events-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-[0.08em] text-zinc-400 border-b border-zinc-200">
            <th className="px-5 py-3 font-semibold">Event</th><th className="px-5 py-3 font-semibold">Date</th>
            <th className="px-5 py-3 font-semibold">Type</th><th className="px-5 py-3 font-semibold text-right">Attendance</th>
            <th className="px-5 py-3 font-semibold text-right">Distance</th><th className="px-5 py-3 font-semibold text-right">Impact</th>
          </tr></thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors duration-150">
                <td className="px-5 py-3 font-medium text-zinc-900 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-zinc-400" /> {e.name}</td>
                <td className="px-5 py-3 text-zinc-500">{e.day}</td>
                <td className="px-5 py-3 text-zinc-500 capitalize">{e.kind.replace("_", " ")}</td>
                <td className="px-5 py-3 text-right tnum text-zinc-500">{e.expected_attendance ? num(e.expected_attendance) : "—"}</td>
                <td className="px-5 py-3 text-right tnum text-zinc-500">{e.distance_km} km</td>
                <td className="px-5 py-3 text-right"><Badge level={e.multiplier >= 1 ? "success" : "warning"}>{e.multiplier >= 1 ? "+" : ""}{Math.round((e.multiplier - 1) * 100)}%</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <style>{`.inp{width:100%;border:1px solid #e4e4e7;border-radius:.5rem;padding:.5rem .625rem;font-size:.875rem} .inp:focus{outline:2px solid #18181b;outline-offset:0}`}</style>
    </div>
  );
}

function L({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">{label}</span><div className="mt-1">{children}</div></label>;
}
